<template>
  <section class="platform-home">
    <!-- SECTION 1 · HERO — copy + carousel, sized to one screen -->
    <section class="section-hero">
      <div class="hero-band">
        <h1 class="hero-title">The website builder for artists who'd rather be in the studio</h1>
        <p class="hero-sub">Build it in 5 minutes. Add a piece in 1.</p>
        <button class="cta-primary" title="Ask for an invite" aria-label="Ask for an invite" @click="scrollToWaitlist">Ask for an invite</button>
        <p class="hero-offer">Free for a year, for the first 10 artists</p>
      </div>

      <div class="hero">
        <ImageCarousel :slides="heroSlides" layout="side-by-side" />
      </div>
    </section>

    <!-- 2 · THE PROMISE — three columns -->
    <div class="promise">
      <div class="promise-col">
        <h3>Simple enough to start now</h3>
        <p>
          Pick a look, add your work, publish. No code, no design software, and
          nothing to design from scratch.
        </p>
      </div>
      <div class="promise-col">
        <h3>…and customizable when you want more</h3>
        <p>
          Change the whole look in a couple of clicks, or take it further with
          your own colors, fonts and pages.
        </p>
      </div>
      <div class="promise-col">
        <h3>Made for artists</h3>
        <p>
          Put your art first. Everything is built to make it look good: full
          screen, zoomable, right on any device.
        </p>
      </div>
    </div>

    <!-- 3 · WHAT YOU GET — the concrete list -->
    <div class="included">
      <h2>What's included</h2>
      <ul class="included-grid">
        <li>Room for 1000 artworks and 10 pages</li>
        <li>Upload and edit in batches, or one work at a time</li>
        <li>Hosting included, nothing else to buy</li>
        <li>A free address at yourname.vitrinis.com</li>
        <li>Connect your own domain instead</li>
        <li>Your own categories and tags, named your way</li>
        <li>Secure by default, with HTTPS and two-factor login</li>
        <li>Free SSL certificate, on your own domain too</li>
        <li>Looks right on phones and tablets</li>
        <li>SEO built in: set it once for the site, or tune any single page</li>
        <li>Shares properly on social media</li>
        <li>No Vitrinis branding on your site</li>
      </ul>
      <button class="cta-quiet" title="Ask for an invite" aria-label="Ask for an invite" @click="scrollToWaitlist">Ask for an invite</button>
    </div>

    <!-- 4 · MADE FOR FINE ART — the differentiator -->
    <div class="fineart">
      <h2>How art is meant to be sold</h2>
      <p class="fineart-lede">
        No cart, no checkout, no commission. A visitor asks about a piece and
        the message comes straight to you, the way a conversation starts.
      </p>
      <div class="fineart-points">
        <p class="fineart-point">Every inquiry comes to you directly. You answer in your own words.</p>
        <p class="fineart-point">No commission, ever. What you sell is yours.</p>
        <p class="fineart-point">For every piece: show a price, hide it, or mark it sold.</p>
      </div>
    </div>

    <!-- 5 · SAFETY — reversibility, then security -->
    <div class="safety">
      <div class="safety-half">
        <h2>You can't break it</h2>
        <p class="safety-lede">
          Nothing you do here is one-way. Change your mind as often as you like.
        </p>
        <ul class="safety-list">
          <li>Archive a piece instead of deleting it, and bring it back exactly where it was</li>
          <li>Keep a page hidden until you're ready, and hide it again any time</li>
          <li>See every change before you publish it</li>
          <li>Undo a reorder, or throw away a draft and go back to what's live</li>
        </ul>
      </div>

      <div class="safety-half">
        <h2>We don't collect what we don't need</h2>
        <p class="safety-lede">
          Your visitors are safe here, and so are you. Nothing sensitive passes
          through your site (no card numbers, no tracking, no profiles built
          from the people who come to see your work), so there's nothing for
          you to guard.
        </p>
        <ul class="safety-list">
          <li>Sign in with a one-time code sent to your email</li>
          <li>Your buyers pay you directly, so no card details ever touch your site</li>
          <li>No trackers following your visitors around the web</li>
        </ul>
      </div>
    </div>

    <!-- 6 · PRICING -->
    <div class="pricing">
      <h2>One plan. Everything included.</h2>
      <p class="price">$8<span class="price-period">/month</span></p>
      <p class="price-sub">No tiers, no feature gates, no upsells. Everything above, for everyone.</p>
      <div class="offer-card">
        <p class="offer-headline">Free for a year, for the first 10 artists</p>
        <p class="offer-body">
          Then the regular price. No payment details needed to start, and we'll
          give you a month's notice before anything changes.
        </p>
      </div>
      <button class="cta-quiet" title="Ask for an invite" aria-label="Ask for an invite" @click="scrollToWaitlist">Ask for an invite</button>
    </div>

    <!-- 7 · ORIGIN — true as written. Expand with the fuller, more personal
         story if we ever want it to carry more weight. -->
    <div class="origin">
      <h2>Why this exists</h2>
      <p>
        Vitrinis was built for one artist who needed it, then opened up to
        everyone else. That's why it works the way it does. The categories
        are yours to invent, a piece can be archived without deleting it, and
        prices can stay off the page. Those aren't features from a list. They came
        from someone who needed them.
      </p>
    </div>

    <!-- 8 · THE SETUP WIZARD — what happens once an invite arrives -->
    <div class="steps-section">
      <h2>Planning to join? Here's what you'll do</h2>
      <p class="steps-lede">
        A guided setup walks you through four steps. That's it, and you're live.
      </p>
      <ol class="steps">
        <li>
          <span class="step-n">1</span>
          <h3>Claim your address</h3>
          <p>yourname.vitrinis.com, yours the moment you sign up.</p>
        </li>
        <li>
          <span class="step-n">2</span>
          <h3>Add your work</h3>
          <p>Add a few pieces to start with. Fill in what you want, skip what you don't.</p>
        </li>
        <li>
          <span class="step-n">3</span>
          <h3>Choose a look</h3>
          <p>Choose your colors and fonts, or generate a look in one click.</p>
        </li>
        <li>
          <span class="step-n">4</span>
          <h3>Publish</h3>
          <p>Share the link. Add a piece later and it appears where it should.</p>
        </li>
      </ol>
    </div>

    <!-- 9 · WAITLIST — the CTA for the invite-only phase -->
    <div id="waitlist" class="waitlist">
      <template v-if="waitlistState === 'done'">
        <p class="waitlist-confirm">Thank you, we will be in touch.</p>
      </template>
      <template v-else>
        <h2>Ask for an invite</h2>
        <p class="waitlist-sub">
          Vitrinis is invite-only for now. Leave your email and we'll get back
          to you.
        </p>
        <div class="waitlist-form">
          <input
            v-model="waitlistEmail"
            type="email"
            class="waitlist-input"
            placeholder="your@email.com"
            maxlength="200"
            @keydown.enter.prevent="submitWaitlist"
          >
          <button
            class="waitlist-btn"
            title="Request an invite"
            aria-label="Request an invite"
            :disabled="waitlistState === 'submitting' || !waitlistEmail.trim()"
            @click="submitWaitlist"
          >
            <span v-if="waitlistState === 'submitting'" class="spinner" />
            {{ waitlistState === 'submitting' ? 'Sending...' : 'Request an invite' }}
          </button>
        </div>
        <p v-if="waitlistError" class="waitlist-error">{{ waitlistError }}</p>
      </template>
    </div>


    <!-- 10 · FAQ -->
    <div class="faq">
      <h2>Questions</h2>
      <div class="faq-grid">
        <div class="faq-item">
          <h3>Do I need to know how to build a website?</h3>
          <p>No. You pick from options and fill things in. There's no code anywhere.</p>
        </div>
        <div class="faq-item">
          <h3>Can I use my own domain?</h3>
          <p>Yes. Buy it wherever you like and point it at your site, and we will walk you through it.</p>
        </div>
        <div class="faq-item">
          <h3>What happens if I stop paying?</h3>
          <p>Your site stays online for 30 days and you keep full access. Nothing is deleted without warning.</p>
        </div>
        <div class="faq-item">
          <h3>I already have a site. Can I move?</h3>
          <p>Yes. Upload your work, point your domain across, and you're moved.</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useHead } from '@unhead/vue'
import { loadFirestore } from '@/utils/loadFirestore'
import ImageCarousel from '@/components/ImageCarousel.vue'

export default {
  name: 'PlatformHome',
  components: { ImageCarousel },
  setup() {
    useHead({ title: 'Vitrinis, a gallery site for artists' })
  },
  data() {
    return {
      waitlistEmail: '',
      waitlistState: 'idle', // idle | submitting | done
      waitlistError: null,
      // PLACEHOLDER IMAGES — capture the real screenshots described in each alt.
      // The three answer, in order: will it look good, will it stay current,
      // can I make it mine.
      heroSlides: [
        {
          src: '/landing-page/auto-sync.jpg',
          alt: 'A gallery set to stay in sync, so new artworks appear in it automatically',
          heading: 'Just upload',
          text: 'Add an artwork and it appears in every gallery that should show it.'
        },
        {
          src: '/landing-page/gallery.jpg',
          alt: 'Gallery layout settings beside a live preview of the gallery',
          heading: 'Make it yours',
          text: 'Change the whole look in a couple of clicks. Preview everything before you publish.'
        },
        {
          src: '/landing-page/demo.jpg',
          alt: 'picavera.com, a real artist site built on Vitrinis',
          heading: 'See what your site could look like',
          text: 'A real site, built on Vitrinis. Have a look around.',
          linkUrl: 'https://picavera.com',
          linkText: 'picavera.com'
        }
      ]
    }
  },
  methods: {
    scrollToWaitlist() {
      document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    async submitWaitlist() {
      this.waitlistError = null
      if (!this.waitlistEmail.trim()) return
      if (!this.validEmail(this.waitlistEmail)) {
        this.waitlistError = 'Please enter a valid email address.'
        return
      }
      this.waitlistState = 'submitting'
      try {
        const { db, addDoc, collection, serverTimestamp } = await loadFirestore()
        await addDoc(collection(db, 'waitlist'), {
          email: this.waitlistEmail.trim(),
          createdAt: serverTimestamp()
        })
        this.waitlistState = 'done'
      } catch (err) {
        console.error('Waitlist submission failed:', err)
        this.waitlistState = 'idle'
        this.waitlistError = 'Something went wrong. Please try again.'
      }
    }
  }
}
</script>

<style scoped>
.platform-home {
  text-align: center;
  max-width: var(--max-width-content);
  margin: 0 auto;
  /* No top padding: section 1 measures itself against the viewport. */
  padding: 0 1.5rem 4rem;
  /* Long unbreakable strings (yourname.vitrinis.com, emails) must not force
     a horizontal scroll on narrow screens. */
  overflow-wrap: break-word;
}

/* Shared section heading */
.platform-home h2 {
  font-family: var(--font-vitrinis);
  font-size: 1.8rem;
  color: var(--color-admin-text);
  margin-bottom: 0.6rem;
}

/* ── Section 1 · Hero ── */
/* One screen exactly (minus the platform navbar), split roughly on the
   golden ratio: copy ~38%, carousel ~62%. Both halves centre their own
   contents, so the split holds at any viewport height. */
.section-hero {
  min-height: calc(100vh - var(--header-height));
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2.5rem 0 3rem;
}

/* Two groups, not four evenly spaced items: the message (title + sub),
   then the action (button + offer). The gap between the groups is the
   largest one on screen; everything inside a group sits tight. */
.hero-band {
  flex: 0 0 auto;
  padding: 0 1rem;
}

.hero-title {
  font-family: var(--font-vitrinis);
  font-size: clamp(1.9rem, 4.5vw, 3rem);
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--color-admin-text);
  margin-bottom: 0.85rem;
  text-wrap: balance;
}

.hero-sub {
  font-size: 1.3rem;
  line-height: 1.5;
  color: var(--color-admin-text);
  max-width: 34rem;
  margin: 0 auto 2rem;
}

.cta-primary {
  padding: 0.9rem 2.25rem;
  border-radius: var(--radius);
  border: none;
  background: var(--color-vitrinis);
  color: var(--color-white);
  font-family: inherit;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.cta-primary:hover {
  opacity: 0.85;
}

/* Sits tight under the button so it reads as part of the CTA. */
.hero-offer {
  margin: 0.85rem 0 0;
  font-size: 0.92rem;
  color: var(--color-muted);
}

/* Secondary CTAs further down the page: same action as the hero button,
   deliberately quieter so the page doesn't read as a funnel. */
.cta-quiet {
  margin-top: 2rem;
  padding: 0.7rem 1.6rem;
  border-radius: var(--radius);
  border: 1px solid var(--color-vitrinis);
  background: none;
  color: var(--color-vitrinis);
  font-family: inherit;
  font-size: 0.98rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.cta-quiet:hover {
  background: var(--color-vitrinis);
  color: var(--color-white);
}


/* Carousel — the larger half of the golden split. The carousel inherits its
   display face and accent from here, so it follows the brand on this page
   and an artist's theme anywhere else. */
.hero {
  font-family: var(--font-vitrinis);
  --color-accent: var(--color-vitrinis);
  flex: 1 1 62%;
  min-height: 0;
  display: flex;
  align-items: center;
  padding: 1.75rem 2.5rem;
  background: var(--color-warm-gray);
  border: 1px solid var(--color-light-gray);
  border-radius: var(--radius-lg);
}

.hero > * {
  width: 100%;
  min-height: 0;
}

/* ── 3 · The promise ── */
.promise {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
  gap: 2.5rem;
  padding: 4rem 0 3rem;
  text-align: left;
}

.promise-col h3 {
  font-family: var(--font-vitrinis);
  font-size: 1.2rem;
  color: var(--color-admin-text);
  margin-bottom: 0.6rem;
  text-wrap: balance;
}

.promise-col p {
  font-size: 0.98rem;
  line-height: 1.6;
  color: var(--color-muted);
}

/* ── 4 · How it works ── */
.steps-section {
  padding: 3rem 0;
}

.steps-lede {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-muted);
  max-width: 34rem;
  margin: 0 auto;
}

.steps {
  list-style: none;
  padding: 0;
  margin: 2rem 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 2rem;
  text-align: left;
}

.step-n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--color-warm-gray-dark);
  color: var(--color-admin-text);
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.8rem;
}

.steps h3 {
  font-family: var(--font-vitrinis);
  font-size: 1.05rem;
  color: var(--color-admin-text);
  margin-bottom: 0.35rem;
}

.steps p {
  font-size: 0.93rem;
  line-height: 1.55;
  color: var(--color-muted);
}

/* ── 5 · What's included ── */
.included {
  padding: 3rem 2rem;
  background: var(--color-warm-gray);
  border: 1px solid var(--color-light-gray);
  border-radius: var(--radius-lg);
}

.included-grid {
  list-style: none;
  padding: 0;
  margin: 1.75rem 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(230px, 100%), 1fr));
  gap: 0.85rem 2rem;
  text-align: left;
}

.included-grid li {
  position: relative;
  padding-left: 1.5rem;
  font-size: 0.98rem;
  line-height: 1.5;
  color: var(--color-admin-text);
}

.included-grid li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--color-vitrinis);
}

/* ── 6 · Made for fine art ── */
.fineart {
  padding: 4rem 0 3rem;
}

.fineart-lede {
  font-size: 1.05rem;
  line-height: 1.65;
  color: var(--color-muted);
  max-width: 38rem;
  margin: 0 auto 2.25rem;
}

.fineart-points {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 1.5rem;
  text-align: left;
}

.fineart-point {
  line-height: 1.6;
  color: var(--color-white);
  padding: 1.5rem 1.75rem;
  background: var(--color-dark-blue);
  border-radius: var(--radius-lg);
}

/* ── 6b · Safety ── */
.safety {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 3rem;
  padding: 3rem 0;
  text-align: left;
}

.safety-half h2 {
  font-size: 1.4rem;
  margin-bottom: 0.6rem;
  text-wrap: balance;
}

.safety-lede {
  font-size: 0.98rem;
  line-height: 1.62;
  color: var(--color-muted);
  margin-bottom: 1.25rem;
}

.safety-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.7rem;
}

.safety-list li {
  position: relative;
  padding-left: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--color-admin-text);
}

.safety-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.5em;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--color-vitrinis);
}

/* ── 7 · Pricing ── */
.pricing {
  padding: 3rem 2rem;
  background: var(--color-warm-gray);
  border: 1px solid var(--color-light-gray);
  border-radius: var(--radius-lg);
}

.price {
  font-family: var(--font-vitrinis);
  font-size: 3rem;
  font-weight: 600;
  color: var(--color-admin-text);
  margin: 1rem 0 0.4rem;
  line-height: 1;
}

.price-period {
  font-size: 1.1rem;
  font-weight: 400;
  color: var(--color-muted);
}

.price-sub {
  color: var(--color-muted);
  font-size: 0.98rem;
}

.offer-card {
  margin: 2rem auto 0;
  max-width: 32rem;
  padding: 1.5rem 1.75rem;
  background: var(--color-admin-bg);
  border: 1px solid var(--color-light-gray);
  border-radius: var(--radius-lg);
}

.offer-headline {
  font-weight: 600;
  color: var(--color-admin-text);
  margin-bottom: 0.5rem;
}

.offer-body {
  font-size: 0.93rem;
  line-height: 1.6;
  color: var(--color-muted);
}

/* ── 7 · Origin ── */
/* Dark panel: the one personal aside on the page, set apart from the
   product sections around it. */
.origin {
  margin: 4rem 0 3rem;
  padding: 3.25rem 2.5rem;
  background: var(--color-dark-blue);
  border-radius: var(--radius-lg);
}

.origin h2 {
  color: var(--color-white);
}

.origin p {
  font-size: 1.05rem;
  line-height: 1.75;
  color: color-mix(in srgb, var(--color-white) 82%, var(--color-dark-blue));
  max-width: 38rem;
  margin: 0 auto;
}

/* ── 9 · Waitlist ── */
.waitlist {
  margin-top: 2rem;
  padding: 3rem 2rem;
  background: var(--color-warm-gray);
  border: 1px solid var(--color-light-gray);
  border-radius: var(--radius-lg);
}

.waitlist-sub {
  color: var(--color-muted);
  margin: 0 auto 1.5rem;
  font-size: 1rem;
  max-width: 28rem;
}

.waitlist-form {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.waitlist-input {
  padding: 0.7rem 1rem;
  border-radius: var(--radius);
  border: 1px solid var(--color-light-gray);
  background: var(--color-admin-bg);
  color: var(--color-admin-text);
  font-size: 1rem;
  width: 280px;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
}

.waitlist-input::placeholder {
  color: var(--color-gray);
}

.waitlist-input:focus {
  border-color: var(--color-vitrinis);
  background: var(--color-white);
}

.waitlist-btn {
  padding: 0.7rem 1.5rem;
  border-radius: var(--radius);
  border: none;
  background: var(--color-vitrinis);
  color: var(--color-white);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 0.2s;
}

.waitlist-btn:hover:not(:disabled) {
  opacity: 0.85;
}

.waitlist-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.waitlist-error {
  color: var(--color-error);
  margin-top: 0.75rem;
  font-size: 0.9rem;
}

.waitlist-confirm {
  font-size: 1.2rem;
  color: var(--color-admin-text);
  font-weight: 500;
}

.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── 10 · FAQ ── */
.faq {
  padding: 4rem 0 1rem;
}

.faq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
  gap: 2rem;
  margin-top: 2rem;
  text-align: left;
}

.faq-item h3 {
  font-family: var(--font-vitrinis);
  font-size: 1.02rem;
  color: var(--color-admin-text);
  margin-bottom: 0.4rem;
}

.faq-item p {
  font-size: 0.93rem;
  line-height: 1.6;
  color: var(--color-muted);
}

@media (max-width: 600px) {
  .hero-band {
    padding: 2rem 0.5rem;
  }

  .hero {
    padding: 2rem 1rem;
  }

  .included,
  .pricing,
  .waitlist,
  .origin {
    padding: 2rem 1rem;
  }

  .platform-home {
    padding: 0 1rem 3rem;
  }

  .safety {
    gap: 2.5rem;
  }

  .offer-card {
    padding: 1.25rem 1rem;
  }

  .platform-home h2 {
    font-size: 1.4rem;
  }

  .price {
    font-size: 2.4rem;
  }

  .waitlist-input {
    width: 100%;
  }

  .waitlist-form {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
